import { Component, lazy, Suspense, type ReactNode } from "react";

import { pageFor } from "./routes";

// Every page loads on demand, so the Guide, the Leaderboard and the doc
// viewer do not download the editor (CodeMirror and all), and the editor
// does not download them.
const App = lazy(() => import("./App"));
const GuidePage = lazy(() => import("./pages/GuidePage").then((m) => ({ default: m.GuidePage })));
const BoardPage = lazy(() => import("./pages/BoardPage").then((m) => ({ default: m.BoardPage })));
const DocPage = lazy(() => import("./pages/DocPage").then((m) => ({ default: m.DocPage })));

/** A page whose code failed to load -- most often a chunk that a new
 *  deploy replaced while this tab was open -- says so instead of going
 *  blank. */
class PageLoadBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <p className="note bad page-loading" role="alert">
        Something went wrong showing this page. If the site was just updated, a reload fixes it.{" "}
        <button className="tiny" onClick={() => window.location.reload()}>reload the page</button>
      </p>
    );
  }
}

/** Which page a URL means (`routes.tsx`'s `pageFor`), rendered without a
 *  router library -- see `routes.tsx` for why. */
export function Root() {
  const page = pageFor(window.location.pathname);
  const Page = page === "guide" ? GuidePage : page === "board" ? BoardPage : page === "doc" ? DocPage : App;
  return (
    <PageLoadBoundary>
      <Suspense fallback={<p className="note page-loading">Loading…</p>}>
        <Page />
      </Suspense>
    </PageLoadBoundary>
  );
}
